/*
  Veylum: the cookie consent notice sits at the bottom of the screen, and stays short on a phone.
  The consent app (Pandectes, <pandectes-cmp>, the only consent manager of the store) opens its notice as a box in the
  middle of the screen, over the title, the price, the sizes and the "Add to cart" button of the product page, and
  over the first product cards. One style sheet added inside its shadow root moves the notice:
  - computer (750 px and up): a slim bar along the bottom edge, the message on the left, the buttons on the right;
  - phone: a bar along the bottom edge, full width, the message then the three buttons on one row (the small title
    line is left out there; the dialog keeps its accessible name).
  The message, its links and the Accept, Decline and Preferences buttons stay those of the app; no element is renamed
  or removed from its markup. While the notice is open, its height is written in --veylum-consent-offset on <html>:
  assets/veylum.css lifts the sticky buy bar and the foot of the cart drawer above it and adds the same space under
  the page, so what sits at the bottom of the screen stays reachable. In a theme preview only, Shopify's own preview
  bar (#PBarNextFrameWrapper) covers the bottom of the screen: the notice then sits just above it. A hidden
  preview bar has a box of zeros: the bar counts only when shown at the bottom, and the offset is bounded by the
  notice's height.
  On a phone, while the visitor has not scrolled yet, the page moves up just enough for the product title and the price
  to sit 12 px above the open notice, and never so far that the title goes under the sticky header. When the small
  line right under the price (.veylum-price-note: USD, taxes included) or the stock line (.product__inventory: In stock,
  Out of stock) is shown below it, the lowest of them is the one kept 12 px above the notice. Once the visitor has
  scrolled, on a computer, on a page without a product, or with a link to a part of the page (#), nothing moves;
  nothing moves back when the notice closes.
*/
(function () {
  var CSS = [
    '.dialog.notice{top:auto;bottom:var(--veylum-consent-lift,0px);left:0;right:0;--translate-x:0px;--translate-y:0px;--transform-origin-y:bottom;--vertical-slide-direction:100%;width:100%;max-width:none!important;border-radius:1.2rem 1.2rem 0 0}',
    '.dialog.notice>.inner{max-height:min(var(--max-height),24rem)}',
    '.dialog.notice .header{display:none}',
    '.dialog.notice .inner>.body{padding:1.2rem 1.6rem 0;font-size:1.3rem;line-height:1.35}',
    '.dialog.notice .body.overflown{-webkit-mask-image:none;mask-image:none}',
    '.dialog.notice .actions{flex-direction:row;gap:0.8rem;padding:1rem 1.6rem 1.2rem}',
    '.dialog.notice .actions .group{display:contents}',
    '.dialog.notice .actions button{flex:1 1 0;min-width:0;min-height:4.4rem;padding:0 0.8rem}',
    '@media (min-width:750px){',
    '.dialog.notice{left:50%;right:auto;width:calc(100% - 3.2rem);max-width:120rem!important;bottom:calc(1.6rem + var(--veylum-consent-lift,0px));--translate-x:-50%;border-radius:1.2rem}',
    '.dialog.notice>.inner{flex-direction:row;align-items:center;flex-wrap:wrap;column-gap:2.4rem;padding:1.2rem 2rem}',
    '.dialog.notice .header{display:flex;flex:0 0 100%;padding:0 0 0.2rem}',
    '.dialog.notice .header>h2>.logo{display:none}',
    '.dialog.notice .inner>.body{flex:1 1 32rem;width:auto;padding:0;font-size:1.4rem}',
    '.dialog.notice .actions{flex:0 0 auto;padding:0}',
    '.dialog.notice .actions button{flex:0 0 auto;min-width:11.2rem;padding:0 1.6rem}',
    '}'
  ].join('');
  var pending = false;
  var watched = null;
  var GAP = 12;
  var kept = {done: false, at: 0};

  function sheet(root) {
    if (root.querySelector('style[data-veylum-consent-banner]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-veylum-consent-banner', '');
    style.textContent = CSS;
    root.appendChild(style);
  }

  function write(name, value) {
    var html = document.documentElement;
    if (html.style.getPropertyValue(name) !== value) html.style.setProperty(name, value);
  }

  // Shopify's preview bar counts only while it is shown along the bottom edge. Hidden (display: none, closed bar, or a
  // page captured without it), its box is all zeros: its top of 0 must never be read as "the whole screen is covered".
  // The lift is bounded by the bar's own height.
  function previewLift(height) {
    var bar = document.getElementById('PBarNextFrameWrapper');
    if (!bar) return 0;
    var b = bar.getBoundingClientRect();
    if (!(b.height > 0) || !(b.top < height) || !(b.bottom >= height - 2)) return 0;
    return Math.max(0, Math.round(Math.min(b.height, height - b.top)));
  }

  // On a phone, the product title and price of the first screen are never left under the open notice.
  // `top` is where the notice's top edge rests once it has slid in (its layout height, transforms ignored).
  function keepProductInfo(height, lift, notice) {
    if (kept.done) return;
    var y = window.pageYOffset || 0;
    if (Math.abs(y - kept.at) > 1 || !(window.innerWidth < 750) || (window.location && window.location.hash) || typeof window.scrollTo !== 'function') {
      kept.done = true;
      return;
    }
    var title = document.querySelector('.product__info-container .product__title');
    var price = document.querySelector('.product__info-container .price');
    if (!title || !price) {
      kept.done = true;
      return;
    }
    var t = title.getBoundingClientRect();
    var p = price.getBoundingClientRect();
    var note = document.querySelector('.product__info-container .veylum-price-note');
    var n = note ? note.getBoundingClientRect() : null;
    var bottom = n && n.height > 0 && n.bottom > p.bottom ? n.bottom : p.bottom;
    var stock = document.querySelector('.product__info-container .product__inventory');
    var s = stock ? stock.getBoundingClientRect() : null;
    if (s && s.height > 0 && s.bottom > bottom) bottom = s.bottom;
    var top = height - lift - (notice.offsetHeight || notice.getBoundingClientRect().height);
    if (!(t.height > 0) || !(p.height > 0) || t.top >= height || bottom + GAP <= top) return;
    var header = document.querySelector('.section-header');
    var room = t.top - (header ? header.offsetHeight || 0 : 0) - GAP;
    var shift = Math.min(Math.ceil(bottom + GAP - top), Math.floor(room));
    if (!(shift > 0)) {
      kept.done = true;
      return;
    }
    kept.at = Math.round(y + shift);
    window.scrollTo(0, kept.at);
  }

  function update() {
    pending = false;
    var height = window.innerHeight;
    var lift = previewLift(height);
    write('--veylum-consent-lift', lift + 'px');
    var host = document.querySelector('pandectes-cmp');
    var root = host && host.shadowRoot;
    var offset = 0;
    var shown = null;
    if (root) {
      if (watched !== root) {
        watched = root;
        new MutationObserver(schedule).observe(root, {childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'class', 'data-open']});
        // The notice zooms or slides in inside the app's shadow root, where the end of its animation is announced: it is
        // measured again once it rests.
        if (typeof root.addEventListener === 'function') {
          root.addEventListener('transitionend', schedule, true);
          root.addEventListener('animationend', schedule, true);
        }
      }
      sheet(root);
      var notice = root.querySelector('dialog.notice');
      if (notice && (notice.open || notice.getAttribute('data-open') === 'true')) {
        var r = notice.getBoundingClientRect();
        // While it zooms or slides in, its box on screen is still short or low: the space is counted from where it
        // rests, its distance to the bottom edge (bottom) plus its full height (offsetHeight, transforms ignored, rounded
        // to the pixel: half a pixel more), so that the sticky buy bar is never placed under its top edge.
        var full = Math.max(r.height, (notice.offsetHeight || 0) + 0.5);
        var cs = typeof window.getComputedStyle === 'function' ? window.getComputedStyle(notice) : null;
        var below = cs && cs.position === 'fixed' ? parseFloat(cs.bottom) : NaN;
        if (!(below >= 0)) below = Math.max(0, height - r.bottom);
        // Only a notice that is really on screen and set along the bottom edge counts. The space kept under the page,
        // the sticky buy bar and the cart drawer is the notice's own height plus what lies under it (preview bar, the
        // 16 px gap of the computer layout), never more, and never more than half the screen.
        if (r.height > 0 && r.top < height && r.bottom > 0 && r.bottom >= height - lift - 32) {
          offset = Math.ceil(Math.min(Math.max(height - r.top, below + full), full + lift + 32, height / 2));
          offset = Math.max(0, offset);
          shown = notice;
        }
      }
    }
    write('--veylum-consent-offset', offset + 'px');
    if (shown) keepProductInfo(height, lift, shown);
  }

  function schedule() {
    if (!pending) {
      pending = true;
      requestAnimationFrame(update);
    }
  }

  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('animationend', schedule, true);
  new MutationObserver(schedule).observe(document.body, {childList: true});
  schedule();
})();
